import { FC } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useMarkActiveCohortAsDefaultedMutation } from '@api/commissions';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import CustomButton from '../../../../../components/CustomButton/CustomButton';

interface ActiveCohortModalProps {
  open: boolean;
  close: (cancel: boolean) => void;
  id: string;
  setShowActiveCohortSidePanel: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => void;
}

const ActiveCohortModal: FC<ActiveCohortModalProps> = ({
  open,
  close,
  setShowActiveCohortSidePanel,
  id,
  refresh,
}) => {
  const dispatch = useDispatch();

  const [markAsDefaulted, { isLoading: isMarkingAsDefaulted }] =
    useMarkActiveCohortAsDefaultedMutation();

  const handleConfirm = async () => {
    try {
      await markAsDefaulted(id).unwrap();

      dispatch(
        setToastMessage({
          message: 'Cohort marked as defaulted',
          type: TOAST_TYPES.SUCCESS,
        }),
      );
      refresh();
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: 'Failed to mark Cohort as Defaulted',
          type: TOAST_TYPES.ERROR,
        }),
      );
    } finally {
      close(false);
      setShowActiveCohortSidePanel(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => close(false)}
      aria-labelledby="send-nda-dialog"
      aria-describedby="send-nda-dialog"
      sx={{
        zIndex: 9999,
        '& .MuiPaper-root': {
          borderRadius: '28px',
          width: 'min(80%, 430px)',
          padding: '1.5rem',
        },
      }}
    >
      <DialogTitle
        id="send-nda-dialog-title"
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: '2rem',
          color: '#162936',
          padding: 0,
          pb: '1rem',
        }}
      >
        Mark as Defaulted
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
        }}
      >
        <DialogContentText
          fontSize={'0.875rem'}
          fontWeight={500}
          lineHeight={'1.25rem'}
          color={'#6C6C6C'}
          id="send-nda-dialog-description"
        >
          Are you sure you want to mark the cohort as defaulted?{' '}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 0,
          pt: '1.5rem',
        }}
      >
        <CustomButton
          color={'#51B8B0'}
          name={isMarkingAsDefaulted ? 'Loading...' : 'Confirm'}
          onClick={handleConfirm}
          disabled={isMarkingAsDefaulted}
        />
        <CustomButton
          color={'#fff'}
          backgroundColor="#2F736E"
          name={'Cancel'}
          onClick={() => close(false)}
          disabled={isMarkingAsDefaulted}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ActiveCohortModal;
